import { isEqual } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { isValueStrictlyEmpty } from '~/utils/strings';

interface DepartmentNameModalProps {
  title: string;
  labelText: string;
  confirmButtonText: string;
  onConfirm: (
    value: string,
    parentDepartmentId?: string | number | null,
  ) => void;
  onCancel: () => void;
  itemsCurrentName?: string;
  itemsParentFolderId?: string | number | null;
}

export const DepartmentNameModal = ({
  title,
  labelText,
  confirmButtonText,
  onConfirm,
  onCancel,
  itemsCurrentName,
  itemsParentFolderId,
}: DepartmentNameModalProps) => {
  const intl = useIntl();
  const [value, setValue] = useState(itemsCurrentName ?? '');

  const hasChanges =
    !isValueStrictlyEmpty(value) && !isEqual(itemsCurrentName, value);

  return (
    <Panel
      mode="floating"
      width="560px"
      title={title}
      hidden={{
        onHide: onCancel,
      }}
      footer={{
        actions: [
          {
            mode: 'chip',
            text: intl.formatMessage({
              id: 'NameModal.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: onCancel,
          },
          {
            id: OnboardingIdType.CreateDepartmentButton,
            disabled: !hasChanges,
            tooltip:
              !isValueStrictlyEmpty(value) && isEqual(value, itemsCurrentName)
                ? intl.formatMessage({
                    id: 'Departments.departmentNameRequired',
                    defaultMessage: 'Please enter a different department name',
                    description: 'Department name required message',
                  })
                : isValueStrictlyEmpty(value)
                  ? intl.formatMessage({
                      id: 'Departments.departmentNameEmpty',
                      defaultMessage: 'Please enter a department name',
                      description: 'Department name required message',
                    })
                  : undefined,
            mode: 'chip',
            level: 'primary',
            text: confirmButtonText,
            onClick: () => onConfirm(value, itemsParentFolderId),
          },
        ],
      }}
      children={
        <FormField<string, false>
          label={labelText}
          input={TextInput}
          name="department name"
          value={value}
          onChange={(newValue) => setValue(newValue ?? '')}
        />
      }
    />
  );
};
